/**
 * QueueStatusIndicator — chrome indicator for the editorial write queue
 * AND session-expiry awareness.
 *
 * Renders a small colored dot in the admin header that surfaces queue
 * durability + auth state at a glance. Click to expand the pending-writes
 * panel (yellow/red queue states only). Tooltip on hover provides detail
 * for every state.
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 3.
 *
 * Visual states (state precedence top-to-bottom):
 *   - Unknown (gray)           → probe pending on mount
 *   - Disabled (gray)          → IndexedDB unavailable (Safari private mode)
 *   - Auth expired (red)       → session null or expires_at ≤ now; can't sync until sign-in
 *   - Failed (red)             → queue has failed_giveup; click panel to retry/discard
 *   - Auth expiring (yellow)   → session.expires_at < now + 5 min
 *   - Slow / Pending (yellow)  → queue has pending or inflight; click for detail
 *   - Green                    → all clear, auth healthy
 *
 * Auth tier rationale (added 2026-05-19): the EditorialQueue.tsx 30-min
 * proactive JWT refresh keeps the session fresh in normal operation, but
 * if the refresh itself fails (cold auth lambda / network blip), the
 * session eventually expires and subsequent mutations hang or fail. This
 * indicator surfaces that BEFORE the editor's next click hangs.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, RotateCw, Trash2, X, Copy } from 'lucide-react';
import { usePending, usePendingList, retry, discard } from '../lib/writeQueue/queue';
import { probeAvailability } from '../lib/writeQueue/db';
import { getClient } from '../lib/supabase';

// The session this indicator watches. It read Proto's client until 2026-09-28 — on a Stage route it
// was reporting the health of a session the page was not using. The admin is Stage-only since CS-5.
const authClient = getClient('stage');

type IndicatorState =
  | { kind: 'unknown' }
  | { kind: 'disabled'; reason: string }
  | { kind: 'auth_expired' }
  | { kind: 'green' }
  | { kind: 'auth_expiring'; minutesLeft: number }
  | { kind: 'yellow'; pendingCount: number; slow: boolean }
  | { kind: 'red'; failedCount: number; pendingCount: number };

type AuthSnapshot =
  | { kind: 'loading' }
  | { kind: 'no_session' }
  | { kind: 'valid'; expiresAt: number }; // unix seconds

function computeState(
  probe: { ok: true } | { ok: false; reason: string } | null,
  pending: ReturnType<typeof usePending>,
  auth: AuthSnapshot,
): IndicatorState {
  if (probe === null) return { kind: 'unknown' };
  if (!probe.ok) return { kind: 'disabled', reason: probe.reason };

  // Auth expiry takes precedence over queue state when expired — nothing
  // will sync until the editor signs in again. Failed_giveup still beats
  // "expiring soon" because the editor needs to see the specific failure
  // (red dot, click for retry/discard).
  const nowSec = Math.floor(Date.now() / 1000);
  if (auth.kind === 'no_session' || (auth.kind === 'valid' && auth.expiresAt <= nowSec)) {
    return { kind: 'auth_expired' };
  }

  if (pending.failedGiveupCount > 0) {
    return {
      kind: 'red',
      failedCount: pending.failedGiveupCount,
      pendingCount: pending.pendingCount + pending.inflightCount,
    };
  }

  if (auth.kind === 'valid') {
    const secsLeft = auth.expiresAt - nowSec;
    if (secsLeft < 300) {
      return { kind: 'auth_expiring', minutesLeft: Math.max(0, Math.floor(secsLeft / 60)) };
    }
  }

  const activeCount = pending.pendingCount + pending.inflightCount;
  if (activeCount === 0) return { kind: 'green' };

  // Compute age at render time (not stored in snapshot — see queue.ts
  // stability contract). Slow = oldest pending mutation > 10s old.
  const oldestAgeMs = pending.oldestPendingCreatedAt === null
    ? null
    : Date.now() - pending.oldestPendingCreatedAt;
  const slow = oldestAgeMs !== null && oldestAgeMs > 10000;
  return { kind: 'yellow', pendingCount: activeCount, slow };
}

interface StyleSpec {
  dotClass: string;
  label: string | null;
  tooltip: string;
  ariaLabel: string;
}

function styleFor(state: IndicatorState): StyleSpec {
  switch (state.kind) {
    case 'unknown':
      return {
        dotClass: 'bg-gray-300',
        label: null,
        tooltip: 'Checking durability…',
        ariaLabel: 'Queue status: initializing',
      };
    case 'disabled':
      return {
        dotClass: 'bg-gray-400',
        label: 'Durability off',
        tooltip: `IndexedDB unavailable (${state.reason}). Your edits won't survive page reload. Switch to a normal browser window for durability.`,
        ariaLabel: 'Queue status: durability disabled',
      };
    case 'auth_expired':
      return {
        dotClass: 'bg-red-500',
        label: 'Sign in to sync',
        tooltip: 'Your session expired. Any pending edits are safe in IndexedDB and will sync once you sign back in. Refresh the page to log in.',
        ariaLabel: 'Queue status: session expired',
      };
    case 'green':
      return {
        dotClass: 'bg-green-500',
        label: null,
        tooltip: 'All edits synced.',
        ariaLabel: 'Queue status: synced',
      };
    case 'auth_expiring':
      return {
        dotClass: 'bg-yellow-400',
        label: `Session: ${state.minutesLeft}m`,
        tooltip: `Your session expires in ~${state.minutesLeft} minute${state.minutesLeft === 1 ? '' : 's'}. Saving keeps your work safe locally — refresh / re-auth before then to avoid sync interruption.`,
        ariaLabel: `Queue status: session expiring in ${state.minutesLeft} minutes`,
      };
    case 'yellow':
      return {
        dotClass: 'bg-yellow-400',
        label: state.slow
          ? `Slow — ${state.pendingCount} pending`
          : `${state.pendingCount} pending`,
        tooltip: state.slow
          ? `${state.pendingCount} edit${state.pendingCount === 1 ? '' : 's'} saved locally; sync is slow. Your work is safe in IndexedDB.`
          : `${state.pendingCount} edit${state.pendingCount === 1 ? '' : 's'} saved locally, syncing in background.`,
        ariaLabel: `Queue status: ${state.pendingCount} pending`,
      };
    case 'red':
      return {
        dotClass: 'bg-red-500',
        label: `${state.failedCount} failed${state.pendingCount > 0 ? ` · ${state.pendingCount} pending` : ''}`,
        tooltip: `${state.failedCount} edit${state.failedCount === 1 ? '' : 's'} failed after 8 retries. Click to retry or discard. Your work is preserved.`,
        ariaLabel: `Queue status: ${state.failedCount} failed`,
      };
  }
}

export function QueueStatusIndicator() {
  const [probe, setProbe] = useState<{ ok: true } | { ok: false; reason: string } | null>(null);
  const pending = usePending();
  const pendingList = usePendingList();
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [auth, setAuth] = useState<AuthSnapshot>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    probeAvailability().then(result => {
      if (!cancelled) setProbe(result);
    });
    return () => { cancelled = true; };
  }, []);

  // Auth polling — refresh the session snapshot every 60s. The existing
  // EditorialQueue.tsx JWT keep-fresh timer refreshes every 30 minutes,
  // so the session's expires_at typically advances faster than this poll
  // catches it. If the refresh fails (cold lambda / network blip), the
  // session ages toward expiry and this poll surfaces the warning before
  // the editor's next mutation hangs.
  //
  // Also listens for onAuthStateChange so sign-in / sign-out / token-
  // refresh events update the snapshot immediately without waiting for
  // the next poll tick.
  useEffect(() => {
    if (!authClient) {
      setAuth({ kind: 'no_session' });
      return;
    }
    let cancelled = false;

    async function refresh() {
      if (!authClient) return;
      try {
        const { data } = await authClient.auth.getSession();
        if (cancelled) return;
        const session = data.session;
        if (!session || !session.expires_at) {
          setAuth({ kind: 'no_session' });
        } else {
          setAuth({ kind: 'valid', expiresAt: session.expires_at });
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[QueueStatusIndicator] auth getSession failed:', (err as Error).message);
          setAuth({ kind: 'no_session' });
        }
      }
    }

    refresh();
    const interval = setInterval(refresh, 60_000);

    const { data: sub } = authClient.auth.onAuthStateChange((_evt, session) => {
      if (cancelled) return;
      if (!session || !session.expires_at) {
        setAuth({ kind: 'no_session' });
      } else {
        setAuth({ kind: 'valid', expiresAt: session.expires_at });
      }
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      sub.subscription.unsubscribe();
    };
  }, []);

  // Close the panel on outside click.
  useEffect(() => {
    if (!panelOpen) return;
    function onDocClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [panelOpen]);

  const state = computeState(probe, pending, auth);
  const spec = styleFor(state);

  // The dot is clickable when there's something to inspect (yellow/red queue
  // states); otherwise it stays a passive status indicator. Auth states
  // (auth_expired, auth_expiring) don't open the panel — the action there
  // is "refresh page / sign in," not "manage pending writes."
  const clickable = state.kind === 'yellow' || state.kind === 'red';

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => clickable && setPanelOpen(v => !v)}
        className={`flex items-center gap-1.5 text-xs select-none px-1.5 py-1 rounded ${
          clickable ? 'hover:bg-surface-light cursor-pointer' : 'cursor-default'
        }`}
        title={spec.tooltip}
        aria-label={spec.ariaLabel}
        aria-expanded={clickable ? panelOpen : undefined}
      >
        <span
          className={`w-2 h-2 rounded-full inline-block ${spec.dotClass}`}
          aria-hidden="true"
        />
        {spec.label && (
          <span className="text-near-black opacity-70 font-medium">{spec.label}</span>
        )}
      </button>

      {/* Click-to-expand panel — Item #3 enhanced. Lists each pending /
       *  inflight / failed_giveup mutation with op + target row +
       *  retry/discard actions for failed_giveup. */}
      {panelOpen && clickable && (
        <div className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto bg-white border border-border rounded-lg shadow-lg z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-xs font-semibold text-near-black">Pending writes</span>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="p-0.5 text-gray-text hover:text-near-black"
              aria-label="Close panel"
            >
              <X size={14} />
            </button>
          </div>
          {pendingList.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-text text-center flex items-center justify-center gap-2">
              <CheckCircle2 size={14} className="text-green-500" />
              All clear.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {pendingList.map(rec => (
                <li key={rec.id} className="px-3 py-2 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 font-medium text-near-black">
                        {rec.status === 'failed_giveup' ? (
                          <AlertCircle size={11} className="text-red-500 flex-shrink-0" />
                        ) : (
                          <Loader2 size={11} className={`text-brand-orange flex-shrink-0 ${rec.status === 'inflight' ? 'animate-spin' : ''}`} />
                        )}
                        <span className="truncate">{rec.op}</span>
                      </div>
                      <div className="text-gray-text mt-0.5 truncate" title={rec.targetRowId}>
                        Row: <span className="font-mono text-[10px]">{rec.targetRowId.slice(0, 8)}…</span>
                        <span className="ml-2">{ageLabel(rec.createdAt)}</span>
                      </div>
                      {rec.lastError && (
                        <div className="text-red-600 mt-0.5 break-words">
                          {rec.lastError}
                        </div>
                      )}
                      {rec.status === 'pending' && rec.attempts > 0 && (
                        <div className="text-gray-text mt-0.5">
                          Attempt {rec.attempts} of 8
                        </div>
                      )}
                    </div>
                    {rec.status === 'failed_giveup' && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            // Copy the full mutation record to clipboard so
                            // the editor can paste it into Slack / GH issue /
                            // wherever they file a report before discarding.
                            const blob = {
                              id: rec.id,
                              op: rec.op,
                              targetRowId: rec.targetRowId,
                              createdAt: new Date(rec.createdAt).toISOString(),
                              attempts: rec.attempts,
                              lastError: rec.lastError,
                              payload: rec.payload,
                            };
                            navigator.clipboard.writeText(JSON.stringify(blob, null, 2)).catch(err =>
                              console.warn('[QueueStatusIndicator] clipboard copy failed:', err),
                            );
                          }}
                          className="p-1 rounded hover:bg-surface-light text-near-black"
                          title="Copy mutation details to clipboard (for bug reports)"
                          aria-label="Copy details"
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => { retry(rec.id); }}
                          className="p-1 rounded hover:bg-surface-light text-near-black"
                          title="Retry this mutation"
                          aria-label="Retry"
                        >
                          <RotateCw size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('Discard this mutation? The optimistic UI change has already been applied locally but will not be persisted.')) {
                              discard(rec.id);
                            }
                          }}
                          className="p-1 rounded hover:bg-red-50 text-red-600"
                          title="Discard — the write will not be retried"
                          aria-label="Discard"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ageLabel(createdAt: number): string {
  const ageMs = Date.now() - createdAt;
  if (ageMs < 1000) return 'just now';
  if (ageMs < 60_000) return `${Math.floor(ageMs / 1000)}s ago`;
  if (ageMs < 3600_000) return `${Math.floor(ageMs / 60_000)}m ago`;
  return `${Math.floor(ageMs / 3600_000)}h ago`;
}
